<template>
  <div
    ref="cardVisualBox"
    class="card-visual-box"
    :class="{ 'card-visual-box--variant-changing': isVariantChanging }"
    @animationend="resetChangingVariant"
  >
    <div class="card-visual-box__inner">
      <a
        :href="currentVariant.productUrl"
        class="card-visual-box__image-wrapper"
        :class="{ 'card-visual-box__image-wrapper--gallery': isCardGalleryTest && isCardSelected }"
        :style="`background-color: ${backgroundColor}`"
      >
        <LabelBadge
          class="card-visual-box__badge"
          :productLabels="card.labels || []"
          :productVariantLabels="currentVariant.labels || []"
          :isBackorderable="currentVariant.isBackorderable"
          :backgroundColor="badgeBackgroundColor"
          :badgeSize="isDesktop ? BADGE_SIZES.normal : BADGE_SIZES.small"
          :isLastChance="isLastChance"
          :isSoldOut="isSoldOut"
        />
        <div
          class="card-visual-box__image-contentful"
          :class="[
            `card-visual-box__image-contentful${getItemSuffix(images[0])}`,
            {
              'card-visual-box__image-contentful--hidden':
                isCardSelected && isCardGalleryTest && images[1],
            },
          ]"
        >
          <ImageContentful
            class="card-visual-box__image"
            :class="{ 'card-visual-box__image--greyed-out': isSoldOut }"
            :alt="cardName"
            :isBackgroundImage="true"
            :lazyload="{ rootMargin: '100%' }"
            :options="imageOptions"
            :src="images[0].mediaUrl"
          />
        </div>

        <template v-if="isCardGalleryTest && isDesktop && images[1]">
          <div
            class="card-visual-box__image-contentful card-visual-box__image-contentful--hover"
            :class="[
              `card-visual-box__image-contentful${getItemSuffix(images[1])}`,
              {
                'card-visual-box__image-contentful--visible': isCardSelected,
                'card-visual-box__image-contentful--hidden': areCarouselImagesLoaded,
              },
            ]"
            :style="`width: ${galleryWidth}px`"
          >
            <ImageContentful
              class="card-visual-box__image card-visual-box__image--hover"
              :class="{ 'card-visual-box__image--greyed-out': isSoldOut }"
              :alt="cardName"
              :isBackgroundImage="true"
              :lazyload="{ rootMargin: '10%' }"
              :options="imageOptions"
              :src="images[1].mediaUrl"
            />
          </div>

          <!-- CardCarousel component lazy loaded on hover-->
          <component
            :is="carouselComponent"
            v-if="isCarouselShown"
            ref="carousel"
            :currentVariant="currentVariant"
            :galleryWidth="galleryWidth"
            :isVisible="isCarouselVisible"
            :product="card"
            :slideIndex="slideIndex"
            @set-slide-index="updateSlideIndex"
            @mounted="setMountedStatus"
          ></component>
        </template>
      </a>
    </div>
    <QuickAdd
      v-if="!isRaffleProduct && !isGiftCardProduct && isDesktop"
      class="card-visual-box__quick-add"
      :variant="currentVariant"
      :onSizesPanelChange="onSizesPanelChange"
      :buttonText="$t('filter.quickadd.quickadd')"
      :context="context"
      v-data-test-id="'quick-add-button'"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { BADGE_SIZES } from '@/config/constants'
import { NO_BORDER_WIDE_VALUES, NO_BORDER_TALL_VALUES } from '@/stores/product'
import { determineIfLastChance, determineIfSoldOut } from '@/helpers/product-availabilty.js'
import { onImagesLoaded } from '@/helpers/event'
import ImageContentful, {
  CONTENTFUL_IMAGE_FIT,
  CONTENTFUL_IMAGE_FORMAT,
} from '@/components/_shared/ImageContentful.vue'
import LabelBadge from '@/components/_shared/LabelBadge/LabelBadge.vue'
import QuickAdd from '@/components/_shared/QuickAdd/QuickAdd.vue'

export const CARD_IMAGE_WIDTH = 480

export default {
  name: 'CardVisualBox',
  components: {
    ImageContentful,
    LabelBadge,
    QuickAdd,
    CardCarousel: () =>
      import(/* webpackChunkName: "card-carousel" */ '@/components/_shared/Cards/CardCarousel.vue'),
  },
  props: {
    currentVariant: {
      type: Object,
      required: true,
    },
    card: {
      type: Object,
      required: true,
    },
    cardName: {
      type: String,
      required: false,
    },
    backgroundColor: {
      type: String,
    },
    badgeBackgroundColor: {
      type: String,
      required: false,
    },
    imageFormat: {
      type: String,
      default: CONTENTFUL_IMAGE_FORMAT.default,
      validator(value) {
        return Object.values(CONTENTFUL_IMAGE_FORMAT).includes(value)
      },
    },
    isCardSelected: {
      type: Boolean,
    },
    isGiftCardProduct: {
      type: Boolean,
    },
    context: {
      type: String,
    },
  },

  data() {
    return {
      areCarouselImagesLoaded: false,
      carouselComponent: 'CardCarousel',
      CONTENTFUL_IMAGE_FIT,
      isCarouselShown: false,
      isMounted: false,
      isVariantChanging: false,
      BADGE_SIZES,
      slideIndex: 0,
      imageOptions: {
        imageCompression: 81,
        format: this.imageFormat,
        fixedWidth: CARD_IMAGE_WIDTH,
        fixedHeight: CARD_IMAGE_WIDTH,
        retinaSupportFactor: 0.25,
        setHeight: false,
        backgroundColor: '#f7f7f7',
      },
    }
  },

  computed: {
    ...mapState('filter', ['isCardGalleryTest']),
    ...mapGetters({
      vpWidth: 'viewport/width',
      isMqLarge: 'viewport/isMqLarge',
    }),

    galleryWidth() {
      return this.isMounted ? this.$refs.cardVisualBox?.offsetWidth : 0
    },

    isRaffleProduct() {
      return this.currentVariant.isRaffle
    },

    isLastChance() {
      return determineIfLastChance(
        this.currentVariant.skus || [],
        this.currentVariant.isBackorderable
      )
    },

    isSoldOut() {
      return determineIfSoldOut(
        this.currentVariant.skus || [],
        this.currentVariant.isRaffle,
        this.currentVariant.hasRaffleEnded
      )
    },

    isDesktop() {
      return this.isMqLarge
    },

    images() {
      return this.currentVariant.productVariantData?.assets
    },

    isCarouselVisible() {
      return this.areCarouselImagesLoaded && this.isCardSelected
    },
  },

  watch: {
    currentVariant() {
      this.isVariantChanging = true
      this.slideIndex = 0
    },

    isCardSelected: 'renderCarousel',
  },

  mounted() {
    this.fetchStoreData()
    this.isMounted = true
  },

  methods: {
    ...mapActions({
      fetchStoreData: 'store/fetchData',
    }),

    getItemSuffix(medium) {
      return NO_BORDER_WIDE_VALUES.includes(medium.borderTouch)
        ? '--no-touch-wide'
        : NO_BORDER_TALL_VALUES.includes(medium.borderTouch)
        ? '--no-touch-tall'
        : ''
    },

    async setMountedStatus() {
      if (this.$refs.carousel) {
        await onImagesLoaded('.card-carousel', { background: '.card-carousel__image--first' })
        this.handleFollowUpActions()
      }
    },

    handleFollowUpActions() {
      this.areCarouselImagesLoaded = true
      this.preloadOtherVariantImages()
    },

    preloadOtherVariantImages() {
      this.card.variants.forEach(variant => {
        const preloadImage = new Image()
        const mediaUrl = variant.productVariantData.assets[1]?.mediaUrl
        const optimizedUrl = On.optimizeContentfulPath({
          ...this.imageOptions,
          desktopSrc: mediaUrl,
        })
        preloadImage.src = 'https:' + optimizedUrl
      })
    },

    renderCarousel() {
      this.isCarouselShown = true
    },

    updateSlideIndex(index) {
      this.slideIndex = index
    },

    onSizesPanelChange(status) {
      this.sizesPanelIsOpen = status
    },

    resetChangingVariant() {
      this.isVariantChanging = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import './CardVisualBox';
</style>
